import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, Trash2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UsersPage() {
  const { t } = useLanguage();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'editor' });
  const [saving, setSaving] = useState(false);

  const loadUsers = useCallback(() => {
    axios.get(`${API}/users`).then(res => setUsers(res.data)).catch(console.error);
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleAdd = async () => {
    if (!form.email || !form.password || !form.name) { toast.error('All fields required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/register`, form);
      toast.success(t('savedSuccess'));
      setAddOpen(false);
      setForm({ email: '', password: '', name: '', role: 'editor' });
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/users/${userId}/role`, { role: newRole });
      toast.success(t('savedSuccess'));
      loadUsers();
    } catch {
      toast.error(t('errorOccurred'));
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await axios.delete(`${API}/users/${toDelete.id}`);
      toast.success(t('deletedSuccess'));
      setDeleteOpen(false);
      setToDelete(null);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errorOccurred'));
    }
  };

  return (
    <div className="fade-in" data-testid="users-admin-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-900">{t('manageUsers')}</h1>
        <Button onClick={() => setAddOpen(true)} className="bg-[#0056A6] hover:bg-[#004E93] shadow-lg shadow-blue-500/20" data-testid="add-user-btn">
          <Plus className="h-4 w-4 me-1.5" />
          {t('addUser')}
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/50">
              <TableHead>{t('name')}</TableHead>
              <TableHead>{t('email')}</TableHead>
              <TableHead className="text-center">{t('role')}</TableHead>
              <TableHead className="text-end">{t('actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map(u => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium text-slate-800">
                  <div className="flex items-center gap-2">
                    {u.name}
                    {u.id === currentUser?.id && (
                      <Badge variant="secondary" className="text-[0.6rem] bg-blue-50 text-[#0056A6] border-0">You</Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-slate-600">{u.email}</TableCell>
                <TableCell className="text-center">
                  <Select value={u.role} onValueChange={v => handleRoleChange(u.id, v)} disabled={u.id === currentUser?.id}>
                    <SelectTrigger className="w-28 mx-auto h-8 text-xs" data-testid={`role-select-${u.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">
                        <div className="flex items-center gap-1.5">
                          <ShieldCheck className="h-3 w-3" /> {t('adminRole')}
                        </div>
                      </SelectItem>
                      <SelectItem value="editor">{t('editorRole')}</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-end">
                  {u.id !== currentUser?.id && (
                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600" onClick={() => { setToDelete(u); setDeleteOpen(true); }} data-testid={`delete-user-${u.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Add User Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle>{t('addUser')}</DialogTitle>
            <DialogDescription>{t('addUser')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('name')}</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="mt-1.5" data-testid="user-name-input" />
            </div>
            <div>
              <Label>{t('email')}</Label>
              <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="mt-1.5" data-testid="user-email-input" />
            </div>
            <div>
              <Label>{t('password')}</Label>
              <Input type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} className="mt-1.5" data-testid="user-password-input" />
            </div>
            <div>
              <Label>{t('role')}</Label>
              <Select value={form.role} onValueChange={v => setForm(p => ({ ...p, role: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">{t('adminRole')}</SelectItem>
                  <SelectItem value="editor">{t('editorRole')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAddOpen(false)}>{t('cancel')}</Button>
            <Button onClick={handleAdd} disabled={saving} className="bg-[#0056A6] hover:bg-[#004E93]" data-testid="user-save-btn">
              {saving ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle>{t('delete')}</DialogTitle>
            <DialogDescription>{t('deleteConfirm')}</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-slate-600">{toDelete?.name} ({toDelete?.email})</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>{t('cancel')}</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="delete-user-confirm-btn">{t('delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
